import { clamp, getImageCrop, rotatedSize } from "./ShareCardLayout";

export const RESIZE_CORNERS = [
  { id: "nw", x: -1, y: -1, label: "top left" },
  { id: "ne", x: 1, y: -1, label: "top right" },
  { id: "se", x: 1, y: 1, label: "bottom right" },
  { id: "sw", x: -1, y: 1, label: "bottom left" },
];

const rotate = (x, y, degrees) => {
  const angle = degrees * Math.PI / 180;
  return { x: x * Math.cos(angle) - y * Math.sin(angle), y: x * Math.sin(angle) + y * Math.cos(angle) };
};
export const normalizeRotation = (angle) => ((angle + 180) % 360 + 360) % 360 - 180;

export const getTransformSize = (element, scene, metrics, trim, textSize) => {
  if (element.kind === "card") {
    const crop = getImageCrop(metrics, trim);
    const width = scene.baseCardWidth * element.scale;
    return { width, height: width * crop.height / crop.width };
  }
  if (element.kind === "image") return { width: element.width, height: element.height };
  return textSize || { width: element.fontSize * Math.max(1, ...String(element.text || "").split("\n").map((line) => line.length)) * 0.65, height: element.fontSize * 1.08 * String(element.text || "").split("\n").length };
};

export const getPreviewPoint = (event, rect, scene) => ({
  x: (event.clientX - rect.left) / rect.width * scene.width,
  y: (event.clientY - rect.top) / rect.height * scene.height,
});

export const beginPreviewTransform = (element, size, point, mode, corner) => {
  const initial = { ...element };
  if (mode === "rotate") return { mode, initial, size, startAngle: Math.atan2(point.y - element.y, point.x - element.x) };
  const diagonal = rotate(size.width * corner.x, size.height * corner.y, element.rotation);
  const anchor = { x: element.x - diagonal.x / 2, y: element.y - diagonal.y / 2 };
  return {
    mode, initial, size, diagonal, anchor,
    pointerOffset: { x: anchor.x + diagonal.x - point.x, y: anchor.y + diagonal.y - point.y },
  };
};

const scaleElement = (element, factor) => {
  if (element.kind === "card") return { ...element, scale: element.scale * factor };
  if (element.kind === "image") return { ...element, width: element.width * factor, height: element.height * factor };
  return { ...element, fontSize: element.fontSize * factor };
};

const getScaleLimits = (element) => {
  if (element.kind === "card") return {
    min: Math.min(1, 0.05 * (element.nativeWidthRatio || 1) / element.scale),
    max: Math.max(1, 10 * (element.nativeWidthRatio || 1) / element.scale),
  };
  if (element.kind === "image") return { min: Math.min(1, 0.1 * element.baseWidth / element.width), max: Math.max(1, 10 * element.baseWidth / element.width) };
  return { min: Math.min(1, 8 / element.fontSize), max: Math.max(1, 600 / element.fontSize) };
};

const maxAnchoredScale = (drag, scene) => {
  const {anchor, diagonal, initial, size} = drag;
  if (anchor.x < 0 || anchor.y < 0 || anchor.x > scene.width || anchor.y > scene.height) return Infinity;
  const xSide = rotate(size.width * Math.sign(rotate(diagonal.x, diagonal.y, -initial.rotation).x), 0, initial.rotation);
  const ySide = { x: diagonal.x - xSide.x, y: diagonal.y - xSide.y };
  let maximum = Infinity;
  for (const side of [xSide, ySide, diagonal]) {
    if (side.x > 1e-8) maximum = Math.min(maximum, (scene.width - anchor.x) / side.x);
    if (side.x < -1e-8) maximum = Math.min(maximum, -anchor.x / side.x);
    if (side.y > 1e-8) maximum = Math.min(maximum, (scene.height - anchor.y) / side.y);
    if (side.y < -1e-8) maximum = Math.min(maximum, -anchor.y / side.y);
  }
  return maximum;
};

const fitTransform = (element, size, scene) => {
  const bounds = rotatedSize(size.width, size.height, element.rotation);
  const factor = Math.min(1, scene.width / bounds.width, scene.height / bounds.height);
  return {
    ...scaleElement(element, factor),
    x: clamp(element.x, bounds.width * factor / 2, scene.width - bounds.width * factor / 2),
    y: clamp(element.y, bounds.height * factor / 2, scene.height - bounds.height * factor / 2),
  };
};

export const updatePreviewTransform = (drag, point, scene, keepInside, snapAngle = false) => {
  const {initial, size} = drag;
  if (drag.mode === "rotate") {
    const angle = Math.atan2(point.y - initial.y, point.x - initial.x);
    const delta = normalizeRotation((angle - drag.startAngle) * 180 / Math.PI);
    let rotation = initial.rotation + delta;
    if (snapAngle) rotation = Math.round(rotation / 15) * 15;
    const next = { ...initial, rotation: normalizeRotation(rotation) };
    return keepInside ? fitTransform(next, size, scene) : next;
  }
  const {diagonal, anchor, pointerOffset} = drag;
  const dx = point.x + pointerOffset.x - anchor.x;
  const dy = point.y + pointerOffset.y - anchor.y;
  const limits = getScaleLimits(initial);
  let factor = clamp((dx * diagonal.x + dy * diagonal.y) / (diagonal.x ** 2 + diagonal.y ** 2), limits.min, limits.max);
  if (keepInside) factor = Math.min(factor, Math.max(0.001, maxAnchoredScale(drag, scene)));
  const next = { ...scaleElement(initial, factor), x: anchor.x + diagonal.x * factor / 2, y: anchor.y + diagonal.y * factor / 2 };
  return keepInside ? fitTransform(next, { width: size.width * factor, height: size.height * factor }, scene) : next;
};

export const getTransformHandleLayout = (element, size, scene, viewport) => {
  const ratio = viewport.width / scene.width;
  const center = { x: element.x * ratio, y: element.y * ratio };
  const halfWidth = size.width * ratio / 2;
  const halfHeight = size.height * ratio / 2;
  const displayWidth = Math.max(26, halfWidth);
  const displayHeight = Math.max(26, halfHeight);
  const project = (x, y) => {
    const vector = rotate(x, y, element.rotation);
    return { x: center.x + vector.x, y: center.y + vector.y };
  };
  const visible = (point) => ({
    x: clamp(point.x, Math.min(22, viewport.width / 2), Math.max(viewport.width / 2, viewport.width - 22)),
    y: clamp(point.y, Math.min(22, viewport.height / 2), Math.max(viewport.height / 2, viewport.height - 22)),
  });
  const corners = RESIZE_CORNERS.map((corner) => ({
    ...corner, outline: project(halfWidth * corner.x, halfHeight * corner.y),
    position: visible(project(displayWidth * corner.x, displayHeight * corner.y)),
  }));
  const candidates = [
    { edge: project(0, -halfHeight), point: project(0, -displayHeight - 34) },
    { edge: project(0, halfHeight), point: project(0, displayHeight + 34) },
    { edge: project(-halfWidth, 0), point: project(-displayWidth - 34, 0) },
    { edge: project(halfWidth, 0), point: project(displayWidth + 34, 0) },
  ];
  const clearance = ({point}) => Math.min(point.x, point.y, viewport.width - point.x, viewport.height - point.y);
  const rotation = candidates.find((candidate) => clearance(candidate) >= 22)
    || candidates.reduce((best, candidate) => clearance(candidate) > clearance(best) ? candidate : best);
  return { corners, rotation: { edge: rotation.edge, position: visible(rotation.point) } };
};
